import axiosInstance from '../services/api/axiosInstance';

const SESSION_KEY = 'qumashi_analytics_session';
const DEDUPE_MS = 30 * 60 * 1000;
const recentKeys = new Map<string, number>();

export type PlatformEventType =
  | 'SESSION_START'
  | 'PAGE_VIEW'
  | 'PRODUCT_VIEW'
  | 'LISTING_VIEW'
  | 'DESIGN_VIEW'
  | 'SELLER_STORE_VISIT'
  | 'CREATOR_STOREFRONT_VISIT'
  | 'REFERRAL_CLICK'
  | 'CREATOR_CODE_APPLIED'
  | 'ADD_TO_CART'
  | 'CHECKOUT_STARTED'
  | 'PAYMENT_ATTEMPTED'
  | 'PAYMENT_SUCCEEDED'
  | 'ORDER_PLACED'
  | 'SEARCH_PERFORMED'
  | 'SEARCH_NO_RESULTS'
  | 'SHOP_FILTER_APPLIED'
  | 'SHOP_FILTER_REMOVED'
  | 'CREATOR_NICHE_SHOP_CLICK';

export type PlatformEventPayload = {
  eventType: PlatformEventType;
  entityType?: string;
  entityId?: string;
  productId?: string;
  listingId?: string;
  designId?: string;
  orderId?: string;
  sellerId?: string;
  creatorProfileId?: string;
  source?: string;
  channel?: string;
  metadata?: Record<string, unknown>;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UUID_PAYLOAD_FIELDS = [
  'entityId',
  'productId',
  'listingId',
  'designId',
  'orderId',
  'sellerId',
  'creatorProfileId',
] as const satisfies ReadonlyArray<keyof PlatformEventPayload>;

export function isAnalyticsUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

/** Drop non-UUID id fields so page-level events do not fail backend validation. */
export function sanitizeAnalyticsPayload(
  payload: PlatformEventPayload,
): PlatformEventPayload {
  const out: PlatformEventPayload = { ...payload };
  for (const field of UUID_PAYLOAD_FIELDS) {
    const value = out[field];
    if (value !== undefined && !isAnalyticsUuid(value)) {
      delete out[field];
    }
  }
  return out;
}

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `guest-${Date.now()}`;
  }
}

function shouldDedupe(eventType: PlatformEventType, entityId?: string): boolean {
  if (!entityId) return false;
  const viewEvents: PlatformEventType[] = [
    'PRODUCT_VIEW',
    'LISTING_VIEW',
    'DESIGN_VIEW',
    'SELLER_STORE_VISIT',
    'CREATOR_STOREFRONT_VISIT',
  ];
  if (!viewEvents.includes(eventType)) return false;
  const key = `${eventType}:${entityId}`;
  const now = Date.now();
  const last = recentKeys.get(key);
  if (last && now - last < DEDUPE_MS) return true;
  recentKeys.set(key, now);
  return false;
}

function inferSource(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('utm_source') ||
      document.referrer?.split('/')[2] ||
      'direct'
    );
  } catch {
    return 'direct';
  }
}

export function trackPlatformEvent(payload: PlatformEventPayload): void {
  try {
    const sanitized = sanitizeAnalyticsPayload(payload);

    if (
      shouldDedupe(
        sanitized.eventType,
        sanitized.entityId ??
          sanitized.productId ??
          sanitized.listingId ??
          sanitized.designId,
      )
    ) {
      return;
    }

    const body = {
      sessionId: getSessionId(),
      source: sanitized.source ?? inferSource(),
      channel: sanitized.channel ?? 'web',
      ...sanitized,
      metadata: {
        path: window.location.pathname,
        ...sanitized.metadata,
      },
    };

    void axiosInstance
      .post('/analytics/events', body, { timeout: 3000 })
      .catch(() => undefined);
  } catch {
    // Analytics must never break UX
  }
}

export function ensureSessionStarted(): void {
  const key = `${SESSION_KEY}:started`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    trackPlatformEvent({ eventType: 'SESSION_START' });
    trackPlatformEvent({ eventType: 'PAGE_VIEW' });
  } catch {
    trackPlatformEvent({ eventType: 'SESSION_START' });
  }
}
